import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  ReplaySubject,
  distinctUntilChanged,
  shareReplay,
  tap,
  type Observable,
} from 'rxjs';
import type { EduExpenseCategory } from '../../shared/models/api/edu-expense-category.model';
import { BudgetType } from '../../shared/models/api/edu-expense.model';
import { EduExpenseCategoryService } from '../../shared/services/api/edu-expense-category.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { budgetTypeTranslation } from '../edu-expense-create/edu-expense-create.helpers';
import { EduExpenseCategoryCreateComponent } from './edu-expense-category-create/edu-expense-category-create.component';
import { EduExpenseCategoryDeleteComponent } from './edu-expense-category-delete/edu-expense-category-delete.component';
import { EduExpenseCategoryEditComponent } from './edu-expense-category-edit/edu-expense-category-edit.component';
@Component({
  selector: 'opa-edu-expense-category-list',
  imports: [
    PorscheDesignSystemModule,
    CommonModule,
    RouterLink,
    LetDirective,
    TranslocoPipe,
    EduExpenseCategoryCreateComponent,
    EduExpenseCategoryEditComponent,
    EduExpenseCategoryDeleteComponent,
  ],
  templateUrl: './edu-expense-category-list.component.html',
  styleUrl: './edu-expense-category-list.component.scss',
})
export class EduExpenseCategoryListComponent {
  readonly mediaQueryService = inject(MediaQueryService);
  private readonly eduExpenseService = inject(EduExpenseCategoryService);

  private readonly loading = new BehaviorSubject(false);
  private readonly eduExpenseCategory = new ReplaySubject<EduExpenseCategory | null>(1);
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  readonly eduExpenseCategory$ = this.eduExpenseCategory.asObservable();
  readonly budgetTypes = Object.values(BudgetType);
  readonly budgetTypeTranslation = budgetTypeTranslation;

  data$ = this.getData$();
  showCreateModal = false;
  showDeleteModal = false;
  showEditModal = false;

  trackEduExpenseCategory(index: number, category: EduExpenseCategory): number {
    return category.id;
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  private getData$(): Observable<EduExpenseCategory[]> {
    this.loading.next(true);

    return this.eduExpenseService.getEduExpenseCategories().pipe(
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openDeleteModal(eduExpenseCategory: EduExpenseCategory): void {
    this.eduExpenseCategory.next(eduExpenseCategory);
    this.showDeleteModal = true;
  }

  closeDeleteModal(eduExpenseCategory?: EduExpenseCategory): void {
    this.showDeleteModal = false;
    if (eduExpenseCategory) this.reloadData();
    this.eduExpenseCategory.next(null);
  }

  openEditModal(eduExpenseCategory: EduExpenseCategory): void {
    this.eduExpenseCategory.next(eduExpenseCategory);
    this.showEditModal = true;
  }

  closeEditModal(success?: boolean): void {
    this.showEditModal = false;
    if (success) this.reloadData();
    this.eduExpenseCategory.next(null);
  }
}
